import { IntlShape } from 'react-intl'

import * as Sentry from '@sentry/react'
import { differenceInWeeks, format, parse, parseISO } from 'date-fns'
import { Iterable, List } from 'immutable'
import { escapeRegExp, get, includes, intersection, isEmpty, isNil, merge } from 'lodash-es'

import { getCurrentYear } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/commonUtils'
import { isCustomerSoleraCoaching } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/customerUtils'
import { getDisplayDate } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import { isImmutable } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/immutableUtils'
import {
  computeRemainderSessions,
  computeRemainderUsed,
  getSessionLimitProgramCategoryByTreatmentOption,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { Programs, PROGRAMS } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  AppLogger,
  createSentryLogger,
  SentryLoggingSDK,
} from '@lyrahealth-inc/shared-app-logic/src/features/sentry/utils'
import { diagnoses } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { EAP_SESSION_REMAINDER_CATEGORY } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useGetEAPSessionCount'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { utilizationQuarters } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { ChargeCopyPrepopInfo, Diagnosis } from '@lyrahealth-inc/shared-app-logic/src/models/transfer/Transfer'
import { Capacity, ProgramCoverageBreakdown, ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { Alert } from '../../data/alertTypes'
import {
  BCC_ROLES,
  BCT_ROLES,
  defaultErrorMessages,
  meetingFormatChargeModalityMap,
  PAYMENT_FORM,
  PAYMENT_TYPES,
  ROLES,
} from '../constants/appConstants'
import * as ROUTES from '../constants/routingConstants'

export const getErrorText = (statusCode: any) => {
  return defaultErrorMessages[statusCode as keyof typeof defaultErrorMessages]
}

// true if at least one of the user's roles exists
// in the array of supported roles for the given route
export const hasPermission = (roles: any, config: any, pathName: any) => {
  const routes = ROUTES
  const route = Object.values(routes).find(({ route }) => pathName === route)
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const isRoleAllowed = intersection(route.roles, roles).length > 0
  const isFeatureEnabled = config?.[(route as any).feature] !== false
  return isRoleAllowed && isFeatureEnabled
}

// true if the role, or roles, being searched for
// exist in the user's roles array
export const hasRole = (roles: any, search: any) => {
  if (!roles) {
    return false
  }

  if (Iterable.isIterable(roles)) {
    roles = roles.toJS()
  }

  if (typeof search === 'string' || search instanceof String) {
    // single string role
    return includes(roles, search)
  } else {
    // array of role strings
    return intersection(roles, search).length > 0
  }
}

// shared amongst: request payment, new payment (provider), new payment (practice)
export const getChargeCopyPrepopInfo = (chargeInfo: any, customers: any) => {
  const prepopInfo: ChargeCopyPrepopInfo = {}
  let initialCompany: CustomerInfo[] = []
  let initialDiagnoses: Diagnosis[] = []
  if (chargeInfo && !chargeInfo.is_guaranteed_time) {
    // cherry pick what we care about and parse as needed
    if (!!chargeInfo.relationship && chargeInfo.relationship !== 'employee') {
      prepopInfo.eligible_member_first_name = chargeInfo.eligible_member_first_name
      prepopInfo.eligible_member_last_name = chargeInfo.eligible_member_last_name
      if (chargeInfo.eligible_member_dob) {
        prepopInfo.eligible_member_dob = getDisplayDate(chargeInfo.eligible_member_dob, 'MM/DD/YYYY')
      }
    }

    prepopInfo.provider_lyra_id = chargeInfo.provider_lyra_id
    prepopInfo.first_name = chargeInfo.first_name
    prepopInfo.last_name = chargeInfo.last_name

    if (chargeInfo.date_of_birth) {
      prepopInfo.date_of_birth = getDisplayDate(chargeInfo.date_of_birth, 'MM/DD/YYYY')
    }
    if (chargeInfo.gender) {
      prepopInfo.gender = mapGenderToLetter(chargeInfo.gender)
    }
    const initialSessionType = chargeInfo.session_type
    if (initialSessionType) {
      prepopInfo.session_type = initialSessionType
    }
    const comp = chargeInfo.company
    if (comp && customers) {
      initialCompany = customers.filter((customer: any) => {
        return customer.value === comp
      })
      if (get(initialCompany, '[0].lyra_code_enabled')) {
        prepopInfo.lyra_code = chargeInfo.lyra_code ?? ''
      }
      prepopInfo.company = initialCompany
    }
    if (chargeInfo.relationship) {
      prepopInfo.relationship = chargeInfo.relationship
    }
    let diag = chargeInfo.diagnosis
    if (diag) {
      diag = diag.toLowerCase()
      const diagArray = diag.replace(/(,\s)/g, ',').split(',')
      initialDiagnoses = diagnoses.filter((obj) => {
        return includes(diagArray, obj.value)
      })
      prepopInfo.diagnosis = initialDiagnoses
    }
  } else if (chargeInfo && chargeInfo.is_guaranteed_time) {
    prepopInfo.is_guaranteed_time = true
    const comp = chargeInfo.company
    if (comp && customers) {
      initialCompany = customers.filter((customer: CustomerInfo) => {
        return customer.value === comp
      })
    }
    prepopInfo.company = initialCompany

    return {
      prepopInfo: prepopInfo,
      initialCompany: initialCompany,
    }
  }

  return {
    prepopInfo: prepopInfo,
    initialCompany: initialCompany,
    initialDiagnoses: initialDiagnoses,
  }
}

export const getBCCharge = ({
  values = {},
  provider,
  client,
  appt,
  episode = {},
  meetingFormat,
  isProviderEngageDA,
  sessionRate,
  sessionType,
  isUsingSelfPay,
  isCustomerIcd10Required,
}: {
  values: any
  provider: any
  client: any
  appt: any
  isProviderEngageDA: boolean
  episode?: any
  sessionsRates?: any
  dateOfBirth?: string
  meetingFormat?: string
  isInternationalCoachingSessionTypeEnabled?: boolean
  sessionRate: string
  sessionType: string
  isUsingSelfPay?: boolean
  /** True when the client's customer requires ICD-10 codes for a non health plan (EAP) session, e.g. Solera */
  isCustomerIcd10Required?: boolean
}) => {
  logToSentry('getBCCharge', {
    action: 'Get BC Charge',
    clientId: client.id,
    values,
  })
  const formData = new FormData()

  const skipKeys = ['modality', 'session_duration']
  Object.entries(values)
    .filter(([key]) => !skipKeys.includes(key))
    .forEach(([key, val]) => {
      if (key === 'diagnosis') val = val ? (val as any).map((d: any) => d.value).toString() : null
      if (key.indexOf('icd10') !== -1) val = (val as any).map((obj: any) => obj.code).join(',')
      if (key === 'attestation_data') val = JSON.stringify(val)
      formData.append(key, val as any)
    })

  if (isProviderEngageDA && values.modality) {
    const modalityValue = typeof values.modality === 'string' ? JSON.parse(values.modality) : values.modality
    formData.append('modality', modalityValue.type)

    // DA Providers can select in person at specific locations
    // and the address's state/zipcode will be added to the modality
    const { state, zipcode } = modalityValue
    if (modalityValue.type === 'in_person' && state && zipcode) {
      formData.append(
        'location',
        JSON.stringify({
          state: modalityValue.state,
          zipcode: modalityValue.zipcode,
        }),
      )
    }
  }

  // There is a separate payment form for guaranteed time
  formData.append('is_guaranteed_time', 'false')

  if (values.is_last_session) {
    const isCompleted = values.episode_state === 'completed'
    formData.append('last_session', isCompleted ? 'true' : 'false')
    formData.append('last_session_terminated', isCompleted ? 'false' : 'true')
  } else {
    formData.append('last_session', 'false')
    formData.append('last_session_terminated', 'false')
  }

  // Populate required fields
  formData.append('provider_lyra_id', provider.id)
  formData.append('company', client.employer)
  formData.append('session_rate', sessionRate)
  formData.append('session_type', sessionType as any)
  formData.append('visit_date', appt.startDate)

  if (isProviderEngageDA) {
    formData.append('session_duration', values.session_duration ?? '0')
  } else {
    formData.append('session_duration', appt.appointmentDuration)
  }

  formData.append('appointment_id', appt.appointmentId)
  if (!isProviderEngageDA) {
    formData.append(
      'modality',
      (meetingFormatChargeModalityMap[appt.meetingFormat as keyof typeof meetingFormatChargeModalityMap] as string) ??
        meetingFormat,
    )
  }
  formData.append('first_name', client.first_name)
  formData.append('last_name', client.last_name)
  formData.append('date_of_birth', client.date_of_birth)
  formData.append('episode_state', (values as any).episode_state || (episode as any).state)

  const relationship = appt.childFirstName ? 'dependent' : 'employee'
  formData.append('relationship', relationship)

  if (client?.lyra_code) {
    // Only send lyra_code if a value exists, to avoid saving a "null" string in the db
    formData.append('lyra_code', client.lyra_code)
  }
  formData.append('patient_lyra_id', client.id)

  if (isUsingSelfPay) {
    formData.set('is_health_plan', 'false') // overwrite the initial value from ICD10sFormMetadata
    formData.append('payment_type', 'self_pay')
  }

  if (isCustomerIcd10Required) {
    // The customer (e.g. Solera) submits the claim to the health plan, Lyra bills the session as EAP.
    // Overwrite the initial value from ICD10sFormMetadata so the ICD-10s don't turn this into a health plan charge.
    formData.set('is_health_plan', 'false')
  }

  return formData
}

/**
 * Sponsoring company options for the payment request form.
 * Solera coaching only customers ("Integrated Benefit Program": enrollment, enrollmentjan ... enrollmentdec) only cover
 * LCC coaching, which DA providers do not support, so they are hidden to avoid confusion with the Solera
 * coaching + therapy + meds customers ("Integrated+ Benefit Program").
 */
export const getPaymentFormSponsoringCompanies = (customers: CustomerInfo[] | undefined) =>
  customers?.filter((customer) => !isCustomerSoleraCoaching(customer.value))

// currently used on request payment. placed here in case we need to share with new payment (provider), new payment (practice)
export const getPatientPrepopInfo = (patient: any, customers: any) => {
  const prepopInfo = {}
  let outcomesAgreed = true
  let employer

  if (patient) {
    const propVal = patient.email_consent ?? true
    outcomesAgreed = propVal === null || propVal === true
    ;(prepopInfo as any).first_name = patient.first_name
    ;(prepopInfo as any).last_name = patient.last_name
    ;(prepopInfo as any).date_of_birth = getDisplayDate(patient.date_of_birth, 'MM/DD/YYYY')

    // cherry pick what we care about and parse as needed
    ;(prepopInfo as any).gender = mapGenderToLetter(patient.gender)
    const comp = patient.employer
    if (comp && customers) {
      employer = customers.filter((customer: any) => {
        return customer.value === comp
      })
      ;(prepopInfo as any).company = employer
    }
  }

  return {
    patientPrepopInfo: prepopInfo,
    patientEmployer: employer,
    patientOutcomesAgreed: outcomesAgreed,
  }
}

const mapGenderToLetter = (genderString: any) => {
  if (!genderString) return genderString // null breaks the below code, but won't break the form feature - field will just remain empty
  switch (genderString.toLowerCase()) {
    case 'male':
    case 'm':
      return 'm'
    case 'female':
    case 'f':
      return 'f'
    case 'unspecified':
    case 'u':
      return 'u'
    case 'decline':
    case 'd':
    case 'prefer not to say':
      return 'd'
    case 'non-binary':
    case 'b':
      return 'b'
    case 'other':
    case 'o':
      return 'o'
    default:
      return genderString
  }
}

export const hasErrorForAction = ($$alerts: Alert[] | List<Alert>, action: string) => {
  const alerts: Alert[] = isImmutable({ value: $$alerts }) ? ($$alerts as List<Alert>).toJS() : $$alerts
  return alerts.some((alert) => alert.action === action)
}

/**
 * Trims a string with a safety check.
 *
 * Useful to trim fields value in forms before validation.
 *
 * @param {string} value The string to trim.
 */
export const trim = function (value: any) {
  return typeof value === 'string' ? value.trim() : value
}

/**
 * Prevents to enter a white space at the beginning of a string value.
 *
 * Useful to prevent users fill a string field with a white space.
 *
 * @param {string} value The string to trim.
 */
export const trimInitialWhiteSpace = function (value: any) {
  if (typeof value === 'string' && value.charAt(0) === ' ') {
    return value.trim()
  } else {
    return value
  }
}

// Clinical data keys that must never reach Sentry: ICD-10 codes (icd10s, icd10_primary_diagnosis,
// icd10_secondary_diagnosis), diagnoses (diagnosis, other_diagnosis) and treatment conditions
// (primary_condition, secondary_condition, other_primary_description, other_secondary_description).
const CLINICAL_DATA_KEY_REGEX = /icd10|diagnos|^(primary|secondary)_condition$|^other_(primary|secondary)_description$/i

/**
 * Returns a copy of `data` with the value of every clinical data key replaced by '[REDACTED]', at any depth.
 * Applied to every `logToSentry` call so close session values, payment request form data and charge responses
 * don't send diagnosis or ICD-10 codes to Sentry.
 */
export const redactClinicalData = (data: unknown, ancestors: Set<object> = new Set()): unknown => {
  if (data === null || typeof data !== 'object') {
    return data
  }
  if (ancestors.has(data)) {
    return '[Circular]'
  }
  ancestors.add(data)
  const result = Array.isArray(data)
    ? data.map((item) => redactClinicalData(item, ancestors))
    : // getOwnPropertyNames (not Object.entries) keeps non-enumerable props such as Error message/stack,
      // matching sanitizeData in createSentryLogger
      Object.fromEntries(
        Object.getOwnPropertyNames(data).map((key) => [
          key,
          CLINICAL_DATA_KEY_REGEX.test(key)
            ? '[REDACTED]'
            : redactClinicalData((data as Record<string, unknown>)[key], ancestors),
        ]),
      )
  ancestors.delete(data)
  return result
}

const sentryLogger = createSentryLogger(Sentry as unknown as SentryLoggingSDK)

export const logToSentry: AppLogger = (sourceCategory, data, level) =>
  sentryLogger(sourceCategory, redactClinicalData(data), level)

export const sanitizeLyraCode = (lyraCode: any) => {
  if (typeof lyraCode === 'string' && lyraCode) {
    return lyraCode.replace(/-|_/g, '')
  } else {
    return ''
  }
}

export const getEpisodeDropdownItemText = (
  intl: IntlShape,
  episode: {
    program_name: string
    start_date: string
    end_date: string | null
  },
) => {
  return `${getProgramDisplayLabel(intl, episode.program_name, 'short')}: ${format(
    parseISO(episode.start_date),
    'MMM yyyy',
  )}${isEmpty(episode.end_date) ? '-current' : `-${format(parseISO(<string>episode.end_date), 'MMM yyyy')}`}`
}

export const prepopulateAttestation = (paymentsData: any) => {
  if (isEmpty(paymentsData)) {
    return
  }
  const mostRecent = paymentsData
    // @ts-expect-error TS(2362): The left-hand side of an arithmetic operation must... Remove this comment to see the full error message
    .toSorted((a: any, b: any) => new Date(b.created_at) - new Date(a.created_at))
    .find((item: any) => item.attestation_data)
  const prepopulateFields = mostRecent?.attestation_data ? JSON.parse(mostRecent.attestation_data) : {}
  return { attestation_data: prepopulateFields ?? {} }
}

export const searchForClients = <TClient extends ClientListClientObject>({
  clients,
  event,
  showAll = false,
  searchValue,
}: {
  clients?: TClient[]
  event?: any
  showAll?: boolean
  searchValue?: string
}) => {
  const target = event?.target
  const searchString = searchValue ? escapeRegExp(searchValue) : escapeRegExp(target?.value?.trim())

  if (isEmpty(searchString) || isNil(searchString)) {
    return showAll ? (clients ?? []) : []
  }

  if (isNil(clients)) {
    return []
  }

  return clients.filter((client) => {
    const isFirstNameMatch = client.first_name?.match(new RegExp(searchString, 'i'))
    const isLastNameMatch = client.last_name?.match(new RegExp(searchString, 'i'))
    const isFullNameMatch = `${client.first_name} ${client.last_name}`.match(new RegExp(searchString, 'i'))
    const isLyraIdMatch = `${client.id}`.match(new RegExp(searchString, 'i'))
    const isPreferredFirstNameMatch = client.preferred_first_name?.match(new RegExp(searchString, 'i'))
    const isPreferredLastNameMatch = client.preferred_last_name?.match(new RegExp(searchString, 'i'))
    const isFullPreferredNameMatch = `${client.preferred_first_name ?? client.first_name} ${
      client.preferred_last_name ?? client.last_name
    }`.match(new RegExp(searchString, 'i'))
    return (
      isFirstNameMatch ||
      isLastNameMatch ||
      isFullNameMatch ||
      isLyraIdMatch ||
      isPreferredFirstNameMatch ||
      isPreferredLastNameMatch ||
      isFullPreferredNameMatch
    )
  })
}

// TODO: This function will eventually be implemented inside the metadata framework.
// When we finish these two tickets, we can remove: PROVIDER-2124 and PROVIDER-2123
export const isOneArrayItemTrue = (items: any, values: any) => {
  let atLeastOneSelected = false
  Object.keys(items).forEach((item) => {
    const splitUp = item.split('.')
    const formName = splitUp[0]
    const fieldName = splitUp[1]
    const form = values[formName]
    if (form && Object.prototype.hasOwnProperty.call(form, fieldName) && form[fieldName] === true) {
      atLeastOneSelected = true
    }
  })
  return atLeastOneSelected
}

// TODO: This function will eventually be implemented inside the metadata framework.
// When we finish these two tickets, we can remove: PROVIDER-2124 and PROVIDER-2123
export const getItemsErrors = (items: any) => {
  let errors = {}
  Object.keys(items).forEach((item) => {
    const splitUp = item.split('.')
    const formName = splitUp[0]
    const fieldName = splitUp[1]
    errors = merge(errors, { [formName]: { [fieldName]: 'At least one field in this group must be selected' } })
  })
  return errors
}

// Used by RequestPayment and HealthPlanInfo
export const convertToFormData = (vals: any) => {
  const formData = new FormData()
  Object.keys(vals).forEach((key) => {
    let val = vals[key]
    if (
      key === PAYMENT_FORM.FIELDS.DATE_OF_BIRTH ||
      key === PAYMENT_FORM.FIELDS.VISIT_DATE ||
      key === PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_DOB
    ) {
      val = getDisplayDate(val, 'YYYY-MM-DD')
    }
    if (
      key === PAYMENT_FORM.FIELDS.DIAGNOSIS ||
      key === PAYMENT_FORM.FIELDS.COMPANY ||
      key === PAYMENT_FORM.FIELDS.PRIMARY_CONDITION ||
      key === PAYMENT_FORM.FIELDS.SECONDARY_CONDITION ||
      key === PAYMENT_FORM.FIELDS.EBT_TYPE
    ) {
      val = val.map((d: any) => d.value).toString()
    }
    if (key === PAYMENT_FORM.FIELDS.LYRA_CODE) {
      val = sanitizeLyraCode(val)
    }
    if (key === PAYMENT_FORM.FIELDS.LOCATION) {
      if (Array.isArray(val)) {
        val = val[0]
      }
      // Only send over the state and zipcode of provider in-person address
      val = JSON.stringify({
        state: val.state,
        zipcode: val.zipcode,
      })
    }
    if (key === 'attestation_data') {
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        val = JSON.stringify(val)
      }
    }
    formData.append(key, val)
  })
  if (vals.payment_type === PAYMENT_TYPES.SELF_PAY) {
    formData.set('is_health_plan', 'false')
  }
  return formData
}

export const getGSCDuration = (startDate: string | undefined, maxDuration: number | null): number | null => {
  if (!startDate) {
    return null
  }

  return Math.min(differenceInWeeks(new Date(), parse(startDate, 'yyyy-MM-dd', new Date())) + 1, maxDuration ?? 0)
}

export const getPreviousQuartersSorted = ({
  availableQuarters,
  howManyQuartersToFetch,
}: {
  availableQuarters?: utilizationQuarters
  howManyQuartersToFetch?: number
}): string[] => {
  if (!availableQuarters) return []
  const retrievedQuartersYears = Object.keys(availableQuarters)
  const sortedQuarters = retrievedQuartersYears.sort((a, b) => {
    const aYearQuarter = a.split('_').reverse().join('') // i.e. 2021Q3
    const bYearQuarter = b.split('_').reverse().join('') // i.e. 2021Q1
    return bYearQuarter < aYearQuarter ? -1 : 1
  })
  if (howManyQuartersToFetch) {
    return sortedQuarters.slice(0, howManyQuartersToFetch)
  }
  return sortedQuarters
}

export const getDropdownItemsFromQuarters = (sortedQuarters: string[], handler: (quarter: string) => void) => {
  return sortedQuarters.map((quarter, index) => {
    let quarterDisplayName = quarter.replace('_', ' ')
    if (index === 0) {
      quarterDisplayName += ' Current'
    }
    return {
      text: quarterDisplayName,
      id: quarter,
      selectHandler: () => {
        handler(quarter)
      },
    }
  })
}

export const getYearMonthDayLocaleByDate = (targetDate: Date) => {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale
  const year = new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(targetDate)
  const month = new Intl.DateTimeFormat(locale, { month: '2-digit' }).format(targetDate)
  const day = new Intl.DateTimeFormat(locale, { day: '2-digit' }).format(targetDate)
  return `${year}-${month}-${day}`
}

export const getTotalCapacity = (allCapacities?: Capacity): number => {
  if (!allCapacities) {
    return 0
  }
  const allPrograms = Object.keys(allCapacities) as (keyof Capacity)[]
  const initialTotalCapacity = 0
  const sumWithInitial = allPrograms.reduce(
    (accumulator, currentProgramName) => accumulator + (allCapacities[currentProgramName] ?? 0),
    initialTotalCapacity,
  )
  return sumWithInitial
}

export const isECDCProvider = (loggedInUser: ProviderUser) => {
  // ECDC is visible to all providers EXCEPT, Practice Providers and Practice Admins unless they are an onsite provider
  return loggedInUser.onsite || !hasRole(loggedInUser.roles, [ROLES.PRACTICE_PROVIDER, ROLES.PRACTICES_ADMIN])
}

export const isEngageDAProvider = (providerRoles: string[]) => {
  return hasRole(providerRoles, [
    ROLES.DA_INDIVIDUAL_THERAPIST,
    ROLES.DA_COUPLES_THERAPIST,
    ROLES.DA_FAMILY_THERAPIST,
    ROLES.DA_CHILD_THERAPIST,
    ROLES.DA_ADOLESCENT_THERAPIST,
  ])
}

export const isEngageICASProvider = (providerRoles: string[]) => {
  return hasRole(providerRoles, [ROLES.ICAS_INDIVIDUAL_THERAPIST])
}

/**
 * CPR-2213: DA Engage providers can have legacy LCT (BlendedCareTherapy) episodes. For session-type
 * resolution — both charge submission and note initial-vs-follow-up classification — treat those
 * episodes as DirectAccessIndividualTherapy so the backend-suggested DA session type is recognized.
 * Single source of truth so the charge and note paths can't drift. Temporary until LCT episodes are
 * migrated off DA Engage providers.
 */
export function resolveDAEngageProgramName(programName: LTProgramName, isProviderEngageDA: boolean): LTProgramName
export function resolveDAEngageProgramName(
  programName: LTProgramName | undefined,
  isProviderEngageDA: boolean,
): LTProgramName | undefined
export function resolveDAEngageProgramName(
  programName: LTProgramName | undefined,
  isProviderEngageDA: boolean,
): LTProgramName | undefined {
  return programName === ProgramNames.BlendedCareTherapy && isProviderEngageDA
    ? ProgramNames.DirectAccessIndividualTherapy
    : programName
}

export const isLCxProvider = (user: ProviderUser | undefined): boolean => {
  return hasRole(user?.roles, [...BCT_ROLES, ...BCC_ROLES, ROLES.LT_PRESCRIBER])
}

export const isProviderUS = (user: ProviderUser | undefined): boolean => {
  return user?.country === 'US'
}

export function shouldCheckDependentEligibilityUsingDependentInfo(
  isChildPatient: boolean,
  customerInfo: CustomerInfo | undefined,
): boolean {
  if (customerInfo == null) {
    return false
  }

  return (
    (!isChildPatient && customerInfo.check_adult_dependent_eligibility_using_dependent_info === 'true') ||
    (isChildPatient && customerInfo.check_child_dependent_eligibility_using_dependent_info === 'true')
  )
}

export function shouldShowPaymentFormEligibleMemberDetails(
  relationship: 'employee' | 'dependent',
  isChildPatient: boolean,
  customerInfo: CustomerInfo | undefined,
) {
  return (
    relationship === 'dependent' &&
    // if dependent is adult and customer prop is enabled to use dependent info for eligibility check,
    // we don't need to collect eligible member information because eligible member is the patient themselves
    // if dependent is minor, we need to collect eligible member for HPI eligibility checking.
    (isChildPatient || !shouldCheckDependentEligibilityUsingDependentInfo(false, customerInfo))
  )
}

export const shouldHideMainNav = (url: string) => {
  const locationsToHideNav = [
    ROUTES.CLIENT_OUTCOMES.route,
    ROUTES.SCHEDULING_ACTIVATE.route,
    ROUTES.LC_CALENDAR_SETUP.route,
    ROUTES.LC_CALENDAR_EDIT.route,
    ROUTES.LC_CALENDAR_EDIT_SELECT_CALENDARS.route,
    ROUTES.LC_CALENDAR_SELECT_CALENDARS.route,
    ROUTES.CLIENTS_CANCEL_SESSION.route,
    ROUTES.CLIENTS_RESCHEDULE_SESSION.route,
    ROUTES.LC_CALENDAR_ACTIVATE.route,
    ROUTES.LC_CALENDAR_SELECT_CALENDARS.route,
    ROUTES.LC_CALENDAR_ACTIVATE_SETUP.route,
    ROUTES.CLIENTS_RESCHEDULE_SESSION_DETAILS.route,
    ROUTES.CLIENT_END_OF_SESSION.route,
    ROUTES.MESSENGER_POPOUT.route,
    ROUTES.CLIENT_NOTE.route,
    ROUTES.CLIENT_NEW_NOTE.route,
    ROUTES.CLIENT_NEW_SAFETY_PLAN.route,
  ]
  return locationsToHideNav.includes(url)
}

export const getClientFullName = (client: ClientListClientObject, isPreferredNameEnabled: boolean) => {
  if (isPreferredNameEnabled) {
    return (
      (client?.preferred_first_name ?? client?.first_name) + ' ' + (client?.preferred_last_name ?? client?.last_name)
    )
  }
  return client?.first_name + ' ' + client?.last_name
}

export const isFullyInViewport = (el: HTMLElement): boolean => {
  const rect = el.getBoundingClientRect()
  const vh = window.innerHeight || document.documentElement.clientHeight
  return rect.top >= 0 && rect.bottom <= vh
}

type ScrollUntilVisibleOptions = {
  idleWaitAfterScrolling?: number
  maxWaitMs?: number
  block?: ScrollLogicalPosition
  inline?: ScrollLogicalPosition
  behavior?: ScrollBehavior
}

/**
 * Scroll the element into view and resolve once it's fully visible or a max timeout elapses.
 * If the element is already fully visible, resolves immediately.
 */
export const scrollUntilVisible = async (el: HTMLElement, options: ScrollUntilVisibleOptions = {}): Promise<void> => {
  if (isFullyInViewport(el)) return

  const {
    idleWaitAfterScrolling = 150,
    maxWaitMs = 2000,
    block = 'center',
    inline = 'nearest',
    behavior = 'smooth',
  } = options

  await new Promise<void>((resolve) => {
    let idleTimer: number | undefined

    const cleanup = () => {
      window.removeEventListener('scroll', onScroll, true)
      if (idleTimer) window.clearTimeout(idleTimer)
      window.clearTimeout(maxTimer)
    }

    const onScroll = () => {
      if (idleTimer) window.clearTimeout(idleTimer)
      idleTimer = window.setTimeout(() => {
        if (isFullyInViewport(el)) {
          cleanup()
          resolve()
        }
      }, idleWaitAfterScrolling)
    }

    const maxTimer = window.setTimeout(() => {
      cleanup()
      resolve()
    }, maxWaitMs)

    window.addEventListener('scroll', onScroll, { passive: true, capture: true } as any)

    // Initiate scroll and kick off idle detection even if no further scroll events fire
    el.scrollIntoView({ block, inline, behavior })
    onScroll()
  })
}
// Return the EAP session-count row category the current episode draws from.
// Mirrors the resolution in `getClientSessionsRemaining` so that callers can locate the row
// whose remaining-session count drove a running-low/out-of-sessions state. Returns the
// remainder ("everything else") category when the episode's program has no explicit limit.
export const getClientSessionLimitCategory = (
  clientDetails: ClientObject | undefined,
  programName: LTProgramName | undefined,
): string | undefined => {
  if (!clientDetails?.sessions_limit_by_program) return undefined

  const treatmentOption = programName
    ? PROGRAMS[programName]?.treatmentOptionProperties?.associatedTriageTreatmentOptions?.[0]
    : undefined

  if (!treatmentOption) return undefined

  const programType = getSessionLimitProgramCategoryByTreatmentOption(treatmentOption)

  return clientDetails.sessions_limit_by_program?.[programType] !== undefined
    ? programType
    : EAP_SESSION_REMAINDER_CATEGORY
}

// Return the number of sessions remaining for the client.
// If the client has session limits per program, return the remaining sessions for the current episode program.
export const getClientSessionsRemaining = (
  clientDetails: ClientObject | undefined,
  programName: LTProgramName | undefined,
) => {
  if (!clientDetails?.sessions_limit_by_program) return clientDetails?.sessions_remaining ?? 0

  // Convert ProgramCustomerPropertyId to TREATMENT_OPTIONS_TYPE
  const treatmentOption = programName
    ? PROGRAMS[programName]?.treatmentOptionProperties?.associatedTriageTreatmentOptions?.[0]
    : undefined

  if (!treatmentOption) {
    return clientDetails?.sessions_remaining ?? 0
  }

  const programType = getSessionLimitProgramCategoryByTreatmentOption(treatmentOption)
  const explicitLimit = clientDetails.sessions_limit_by_program?.[programType]

  if (explicitLimit !== undefined) {
    const usedSessionCount = clientDetails.sessions_used_by_program?.[programType] ?? 0
    return Math.max(explicitLimit - usedSessionCount, 0)
  }

  // The program category is not explicitly configured (e.g. numberOfSessionsByProgram: `{neurodiversity: 8}`
  // listed for a customer with general EAP coverage). Fall back to the remainder ("everything
  // else") bucket: total sessions minus the explicit per-program limits, minus usage for
  // explicitly-configured programs only (usage for non-configured programs already counts
  // against the remainder via `sessions_used`).
  const remainderLimit = computeRemainderSessions(clientDetails.sessions_limit, clientDetails.sessions_limit_by_program)
  const remainderUsed = computeRemainderUsed(
    clientDetails.sessions_used,
    clientDetails.sessions_limit_by_program,
    clientDetails.sessions_used_by_program,
  )
  return Math.max(remainderLimit - remainderUsed, 0)
}

/**
 * Returns true when ALL preconditions for the HPI + self-pay hold:
 *   - `isSelfPayEnabled` flag is on
 *   - customer supports self-pay (`customer_support_self_pay`)
 *   - program is `selfPayEligible`
 *   - program's coverage is HPI-only for the current year (BHB supported, no EAP, no specialty BHB)
 */
export const isHPIAndSelfPaySupported = ({
  clientDetails,
  programName,
  programCoverageBreakdown,
  isSelfPayEnabled,
}: {
  clientDetails: ClientObject | undefined
  programName: string | undefined
  programCoverageBreakdown: ProgramCoverageBreakdown | undefined
  isSelfPayEnabled: boolean | undefined
}): boolean => {
  if (!isSelfPayEnabled || !clientDetails?.customer_support_self_pay) {
    return false
  }
  const programConfig = Programs.getProgramConfigByName(programName)
  if (!programConfig?.selfPayEligible) {
    return false
  }
  const coverageKey = programConfig.customerPropertyId
  const yearCoverage = programCoverageBreakdown?.[getCurrentYear()]
  const coverage = coverageKey ? yearCoverage?.[coverageKey as keyof typeof yearCoverage] : undefined
  return !!coverage?.bhbSupported && !coverage?.eapSupported && !coverage?.specialtyBhbSupported
}
